import { Component, EventEmitter, Input, OnInit, Output, Self, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { EquipmentManagenementService } from 'src/app/services/equipmentmanagement.service';
import { SaveService } from 'src/app/services/save.service';
import { DefaultValuesService } from '../../../../../../services/defaultvalues.service';
import { forkJoin, of, takeUntil, Observable } from 'rxjs';
import { DestroyService } from 'src/app/services/destroyservice';
import { SoftwareReleaseItem } from '../../types/software-release';
import { EmtAllowedSoftwareReleaseLevelDto, EmtAllowedSoftwareReleaseType, EmtAllowedSoftwareReleaseValue } from 'src/app/interfaces/emt/emt-allowed-softwarer-release';
import { MatSort } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import { EmtMandatoryVersionSoftwareReleaseDto } from '../../../../../../interfaces/emt/emt_mandatory-update-software-release';

@Component({
    selector: 'mandatory-version-list',
    templateUrl: './mandatory-version-list.component.html',
    styleUrls: ['./mandatory-version-list.component.less', '../../../../../../style/new-generic-styles.component.less'],
    encapsulation: ViewEncapsulation.None,
    providers: [DestroyService],
    standalone: false
})
export class MandatoryVersionListComponent implements OnInit {
    public readonly ENVIRONMENT = "environment";
    public readonly TENANT = "tenant";
    public readonly COLUMNS = {
        [this.ENVIRONMENT]: ['familyCode', 'version', 'releaseDate', 'environment', 'actions'],
        [this.TENANT]: ['familyCode', 'version', 'releaseDate', 'tenant', 'environment', 'actions'],
    };

    public form: FormGroup;
    public columnsToDisplay = this.COLUMNS[this.ENVIRONMENT];

    @Input()
    public softwareReleases: SoftwareReleaseItem[] = []
    public availableReleasesArray: any = [];

    @Input()
    public familyCodes: Map<number, { familyCode: string; familyCodeName: string }> = new Map();

    public selectedLevel?: EmtAllowedSoftwareReleaseLevelDto;
    @ViewChild(MatSort) sort!: MatSort;

    public dataSource = new MatTableDataSource<any>();

    constructor(
        private readonly emtService: EquipmentManagenementService,
        private readonly toastrService: ToastrService,
        private readonly saveService: SaveService,
        private readonly formBuilder: FormBuilder,
        public readonly defaults: DefaultValuesService,
        private cdr: ChangeDetectorRef,
        @Self() private readonly destroy$: DestroyService,
        public datePipe: DatePipe,
    ) {
        this.form = this.formBuilder.group({
            rows: this.formBuilder.array([])
        });
    }

    public ngOnInit() {

        this.defaults.stageMessageChanges$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.selectedLevel = undefined;
                this.clearForm();
            });

        this.clearForm();
        this.updateHeaders();

        if (this.selectedLevel) {
            this.loadSoftwareReleases();
        }
    }

    public changeSelectedLevel(newLevel: EmtAllowedSoftwareReleaseLevelDto) {
        this.selectedLevel = newLevel;
        this.clearForm();
        this.updateHeaders();
        this.loadSoftwareReleases();
    }

    public onRowEditInit(row: any) {
        const formIndex = this.findFormArrayIndex(row);
        if (formIndex === -1) return;
        const rows = this.form.get('rows') as FormArray;
        rows.at(formIndex).get('onEdit')?.patchValue(true);
        this.dataSource = new MatTableDataSource(rows.value);
        this.dataSource.sortData = this.getSortDataFn();
        this.dataSource.sort = this.sort;
        this.cdr.detectChanges();
    }

    public onRowEditCancel(row: any) {
        const formIndex = this.findFormArrayIndex(row);
        if (formIndex === -1) return;
        this.updateFormValue(formIndex, 'onEdit', false);
        this.resetFormValue(formIndex, 'environmentValue');
        this.resetFormValue(formIndex, 'tenantValue');
        const rows = this.form.get('rows') as FormArray;
        this.dataSource = new MatTableDataSource(rows.value);
        this.dataSource.sortData = this.getSortDataFn();
        this.dataSource.sort = this.sort;
        this.cdr.detectChanges();
    }

    private getSortDataFn() {
        return (data: any[], sort: MatSort) => {
            if (!sort.active || sort.direction === '') {
                return data;
            }
            const isAsc = sort.direction === 'asc';
            return data.slice().sort((a, b) => {
                switch (sort.active) {
                    case 'version':
                        return (a.version || '').localeCompare(b.version || '') * (isAsc ? 1 : -1);
                    case 'releaseDate':
                        return ((a.releaseDate ? new Date(a.releaseDate).getTime() : 0) - (b.releaseDate ? new Date(b.releaseDate).getTime() : 0)) * (isAsc ? 1 : -1);
                    case 'familyCode':
                        return (a.familyCode || '').localeCompare(b.familyCode || '') * (isAsc ? 1 : -1);
                    case 'environment':
                        return (this.formatEnvironmentValue(a.environmentValue) || '')
                            .localeCompare(this.formatEnvironmentValue(b.environmentValue) || '') * (isAsc ? 1 : -1);
                    case 'tenant':
                        return (this.formatTenantValue(a.tenantValue, a.environmentValue) || '')
                            .localeCompare(this.formatTenantValue(b.tenantValue, b.environmentValue) || '') * (isAsc ? 1 : -1);
                    default:
                        return 0;
                }
            });
        };
    }

    public onSave() {
        const modifiedRows = this.form.value.rows
            .filter((x: any) => x.onEdit);

        if (!modifiedRows.length) {
            return;
        }

        // Determine if we're at tenant level or environment level
        const isTenantLevel = !!this.selectedLevel?.tenantId;

        const saveRequests: Observable<any> [] = [];

        if (isTenantLevel) {
            const environmentRequest = {
                environment: this.selectedLevel!.environment,
                tenantId: null,
                softwaresReleases: modifiedRows.map(x => ({
                    id: x.id,
                    type: x.type,
                    mandatoryVersionToUpdateId: x.environmentValue,
                })),
            };

            const tenantRequest = {
                environment: this.selectedLevel!.environment,
                tenantId: this.selectedLevel!.tenantId,
                softwaresReleases: modifiedRows.map(x => ({
                    id: x.id,
                    type: x.type,
                    mandatoryVersionToUpdateId: x.tenantValue,
                })),
            };

            saveRequests.push(
                this.emtService.updateMandatoryVersionControllerSoftwareReleases(environmentRequest),
                this.emtService.updateMandatoryVersionControllerSoftwareReleases(tenantRequest)
            );
        } else {
            const environmentRequest = {
                environment: this.selectedLevel!.environment,
                tenantId: null,
                softwaresReleases: modifiedRows.map(x => ({
                    id: x.id,
                    type: x.type,
                    mandatoryVersionToUpdateId: x.environmentValue,
                })),
            };

            saveRequests.push(
                this.emtService.updateMandatoryVersionControllerSoftwareReleases(environmentRequest)
            );
        }

        // Execute all save requests
        forkJoin(saveRequests).subscribe({
            next: () => {
                this.toastrService.success('Mandatory Versions to Update saved.');
                this.loadSoftwareReleases();
            },
            error: (error) => {
                this.toastrService.error(error.error);
            },
        });
    }

    public hasChanges() {
        return this.form.value.rows.some(x => x.onEdit);
    }

    public isTenantLevel(): boolean {
        return !!this.selectedLevel?.tenantId;
    }

    public formatEnvironmentValue(value: number) {
        const release = this.softwareReleases.find(r => r.id === value);
        return release ? release.name : 'Not set';
    }

    public formatTenantValue(tenantValue: number, environmentValue: number) {
        if (!tenantValue || tenantValue === -1) {
            return this.formatEnvironmentValue(environmentValue) + ' (inherited)';
        }
        else {
            const release = this.softwareReleases.find(r => r.id === tenantValue);
            return release ? release.name : 'Not set';
        }
    }

    public clearForm() {
        this.form = this.formBuilder.group({
            rows: this.formBuilder.array([]),
        });
        this.form.reset();
        this.dataSource = new MatTableDataSource<any>([]);
        this.dataSource.sort = this.sort;
        this.cdr.detectChanges();
    }

    private updateHeaders() {
        if (this.selectedLevel && this.selectedLevel.tenantId) {
            this.columnsToDisplay = this.COLUMNS[this.TENANT];
        } else {
            this.columnsToDisplay = this.COLUMNS[this.ENVIRONMENT];
        }
    }

    private loadSoftwareReleases() {
        const sortedReleases = [...this.softwareReleases]
            .filter(release => release.name && release.name.trim() !== '') // Filter out releases with empty names
            .sort((a, b) => {
                const dateA = a.releaseDate ? new Date(a.releaseDate).getTime() : null;
                const dateB = b.releaseDate ? new Date(b.releaseDate).getTime() : null;

                if (dateA !== null && dateB !== null) {
                    return dateB - dateA;
                }
                if (dateA === null && dateB === null) {
                    return (a.version || '').localeCompare(b.version || '');
                }
                return dateA === null ? 1 : -1;
            });

        this.availableReleasesArray = [
            { id: null, version: 'Not set', name: 'Not set' },
            ...sortedReleases.map(release => ({
                id: release.id,
                version: release.name,
                name: release.name
            }))
        ];

        const isTenant = !!this.selectedLevel?.tenantId;
        const getEnvironmentReleases$ = this.emtService.getMandatoryVersionControllerSoftwareRelease({ ...this.selectedLevel!, tenantId: undefined })
        const getTenantReleases$ = isTenant
            ? this.emtService.getMandatoryVersionControllerSoftwareRelease(this.selectedLevel!)
            : of(new Array<EmtMandatoryVersionSoftwareReleaseDto>());

        forkJoin([getEnvironmentReleases$, getTenantReleases$])
            .subscribe({
                next: ([environmentReleases, tenantReleases]) => {
                    this.fillForm(environmentReleases, tenantReleases);
                },
                error: () => {
                    this.toastrService.error("Unable to load software releases!");
                }
            });
    }

    public getFormControl(row: any, field: string): FormControl<any> {
        const rows = this.form.get('rows') as FormArray;
        const formIndex = this.findFormArrayIndex(row);
        if (formIndex === -1) {
            throw new Error(`FormControl '${field}' not found for row with id=${row.id} and type=${row.type}`);
        }
        const control = rows.at(formIndex).get(field);
        if (!control) {
            throw new Error(`FormControl '${field}' not found in row ${formIndex}`);
        }
        return control as FormControl<any>;
    }

    private fillForm(environmentReleases: EmtMandatoryVersionSoftwareReleaseDto[], tenantReleases: EmtMandatoryVersionSoftwareReleaseDto[]) {
        const releaseKey = (r: { type: EmtAllowedSoftwareReleaseType, id: number }) => `${r.type}-${r.id}`;
        const environmentReleasesMap = new Map(environmentReleases.map(r => [releaseKey(r), r]));
        const tenantReleasesMap = new Map(tenantReleases.map(r => [releaseKey(r), r]));

        this.form = this.formBuilder.group({
            rows: this.formBuilder.array(
                this.softwareReleases
                    .filter(release => {
                        const key = releaseKey(release);
                        return environmentReleasesMap.has(key) || tenantReleasesMap.has(key);
                    })
                    .map(release => {
                        const key = releaseKey(release);
                        const environmentValue = environmentReleasesMap.get(key)?.mandatoryVersionToUpdateId;
                        const tenantValue = tenantReleasesMap.get(key)?.mandatoryVersionToUpdateId;

                        return this.formBuilder.group({
                            id: new FormControl(release.id),
                            type: new FormControl(release.type),
                            version: new FormControl(release.version),
                            familyCode: new FormControl(this.familyCodes.get(release.familyCodeId)?.familyCode + ' - ' + this.familyCodes.get(release.familyCodeId)?.familyCodeName),
                            releaseDate: new FormControl(release.releaseDate),
                            name: new FormControl(release.name),
                            environmentValue: new FormControl(environmentValue),
                            tenantValue: new FormControl(tenantValue),
                            onEdit: new FormControl(false),
                            unmodified: new FormControl({ environmentValue, tenantValue })
                        });
                    }))
        });
        this.dataSource = new MatTableDataSource((this.form.get('rows') as FormArray).value);
        this.dataSource.sortData = this.getSortDataFn();
        this.dataSource.sort = this.sort;
        this.cdr.detectChanges();
    }

    private updateFormValue(index: number, fieldName: string, newValue: any) {
        const rows = this.form.get('rows') as FormArray;
        const row = rows.at(index);
        row.get(fieldName)?.setValue(newValue);
    }

    private resetFormValue(index: number, fieldName: string) {
        const rows = this.form.get('rows') as FormArray;
        const row = rows.at(index);
        const value = row.value.unmodified[fieldName];
        row.get(fieldName)?.setValue(value);
    }

    private findFormArrayIndex(row: any): number {
        const rows = this.form.get('rows') as FormArray;
        return rows.controls.findIndex(control =>
            control.value.id === row.id && control.value.type === row.type
        );
    }
}